import { useEffect, useState } from 'react';

import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import moment from 'moment';

import { Button } from '@/components/TailwindComponents/base/Button';
import { Calendar } from '@/components/TailwindComponents/base/Calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/TailwindComponents/base/Popover/Popover';
import { cn } from '@/components/TailwindComponents/lib/utils';
import { DATE_FORMAT } from '@/config';

import { MonthYearPicker } from '../MonthYearPicker/MonthYearPicker';

interface DatePickerProps {
  onSelect?: (date: Date | undefined) => void;
  format?: string;
  date?: Date;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  portalContainer?: HTMLElement | null;
  // MonthYearPicker specific props
  showMonthYearPicker?: boolean;
  monthYearPickerProps?: {
    showClearBtn?: boolean;
  };
  // Custom display formatting props
  customDisplayFormat?: (date: Date) => string;
  customPlaceholder?: string;
  /**
   * @description If true, shows a clear (X) button when a date is selected.
   */
  clearable?: boolean;
}

export function DatePicker({
  onSelect,
  date,
  minDate,
  maxDate,
  format = DATE_FORMAT,
  className,
  disabled,
  placeholder = 'MM/DD/YYYY',
  showMonthYearPicker = false,
  monthYearPickerProps = {},
  customDisplayFormat,
  customPlaceholder,
  portalContainer,
  clearable = false,
}: DatePickerProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [displayMonth, setDisplayMonth] = useState<Date>(date || new Date());

  useEffect(() => {
    if (
      (date && (!selectedDate || date.getTime() !== selectedDate.getTime())) ||
      (!date && selectedDate)
    ) {
      setSelectedDate(date);
    }
  }, [date]);

  const handleOnSelect = (dateValue: Date | undefined) => {
    setSelectedDate(dateValue);
    onSelect?.(dateValue);
    if (dateValue) setOpen(false); // Close popover when a date is selected
  };

  const handleMonthYearChange = (value: string | Date | undefined) => {
    if (value) {
      let newDate: Date;
      if (typeof value === 'string') {
        // Parse MM/yyyy string format
        const [month, year] = value.split('/');
        newDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      } else {
        newDate = value;
      }
      setDisplayMonth(newDate);
    }
  };

  const CustomCaption = ({
    displayMonth: captionDisplayMonth,
  }: {
    displayMonth: Date;
  }) => {
    if (showMonthYearPicker) {
      return (
        <div className="flex justify-center pt-1 relative items-center">
          {/* Previous Month Button */}
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-1"
            onClick={() => {
              const prevMonth = new Date(captionDisplayMonth);
              prevMonth.setMonth(prevMonth.getMonth() - 1);
              setDisplayMonth(prevMonth);
            }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* MonthYearPicker */}
          <MonthYearPicker
            value={captionDisplayMonth}
            onSelect={handleMonthYearChange}
            className="h-8 text-sm font-medium justify-center border-0 shadow-none p-0 hover:bg-transparent"
            {...monthYearPickerProps}
          />

          {/* Next Month Button */}
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-1"
            onClick={() => {
              const nextMonth = new Date(captionDisplayMonth);
              nextMonth.setMonth(nextMonth.getMonth() + 1);
              setDisplayMonth(nextMonth);
            }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      );
    }
    return null;
  };

  const showClearButton = clearable && selectedDate && !disabled;

  const handleClear = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedDate(undefined);
    onSelect?.(undefined);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full text-left font-normal justify-between border-input text-foreground',
            !selectedDate && 'text-muted-foreground',
            className,
          )}
          disabled={disabled}
        >
          <span className="flex items-center gap-2 flex-1 truncate">
            {customDisplayFormat && <CalendarIcon className="h-4 w-4" />}
            {selectedDate ? (
              customDisplayFormat ? (
                customDisplayFormat(selectedDate)
              ) : (
                moment(selectedDate).format(format)
              )
            ) : (
              <span>{customPlaceholder || placeholder}</span>
            )}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {showClearButton && (
              <span
                className="flex items-center cursor-pointer hover:text-red-500 transition-colors"
                onClick={handleClear}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                tabIndex={0}
                role="button"
                aria-label="Clear date"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleClear(e);
                  }
                }}
              >
                <X className="w-4 h-4" />
              </span>
            )}
            {!customDisplayFormat && <CalendarIcon className="h-4 w-4" />}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" container={portalContainer}>
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(dateValue) => handleOnSelect(dateValue)}
          initialFocus
          fromDate={minDate}
          toDate={maxDate}
          month={displayMonth}
          onMonthChange={setDisplayMonth}
          className={className}
          classNames={{
            day_selected:
              'bg-[#EE3E5C] hover:bg-[#EE3E5C] text-white hover:text-white focus:bg-[#EE3E5C]',
          }}
          components={{
            Caption: showMonthYearPicker
              ? ({ displayMonth: captionDisplayMonth }) => (
                  <CustomCaption displayMonth={captionDisplayMonth} />
                )
              : undefined,
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
